import { Container, Graphics, Text } from 'pixi.js';
import type { Scene } from '../core/Scene';
import type { InputSnapshot } from '../input/actions';
import type { GameContext } from '../GameContext';
import { Palette, PROC_COLOR } from '../render/Palette';
import { ARTIFACTS_BY_ID, type ArtifactProc } from '../sim/artifacts/ArtifactDefs';
import type { EquippedArtifact, RunState } from '../sim/run/RunState';

const PANEL_W = 480;
const ROW_H = 46;

/**
 * At-capacity artifact pickup: choose which equipped artifact to swap out
 * for the new one, or walk away. Brought artifacts are SAFE and locked;
 * CURSED artifacts are BOUND (unswappable until cleansed at a vault altar).
 * The offered artifact is UNIDENTIFIED — drops don't reveal what they are
 * until they're in your hands, so a swap is a real gamble.
 */
export class ArtifactSwapOverlay implements Scene {
  readonly root = new Container();
  readonly blocksUpdateBelow = true;

  private focus = 0;
  private rows: { y: number; artifact: EquippedArtifact; locked: boolean }[] = [];
  private focusRing = new Graphics();
  private acceptDelay = 8;

  constructor(
    ctx: GameContext,
    runState: RunState,
    _offered: { artifactId: string; quality: number; proc: ArtifactProc; curseId: string | null },
    private readonly onSwap: (oldArtifactId: string) => void,
    private readonly onCancel: () => void,
    private readonly popSelf: () => void,
  ) {
    const w = ctx.app.screen.width;
    const h = ctx.app.screen.height;
    const equipped = runState.artifacts;
    const panelH = 150 + equipped.length * ROW_H;
    const px = (w - PANEL_W) / 2;
    const py = (h - panelH) / 2;

    this.root.addChild(new Graphics().rect(0, 0, w, h).fill({ color: 0x05060c, alpha: 0.78 }));
    this.root.addChild(new Graphics()
      .roundRect(px, py, PANEL_W, panelH, 10)
      .fill(Palette.hudPanel)
      .stroke({ width: 2, color: Palette.gold, alpha: 0.6 }));

    const title = new Text({
      text: 'HANDS FULL',
      style: {
        fontFamily: 'monospace', fontSize: 20, fontWeight: 'bold',
        fill: Palette.hudText, letterSpacing: 3,
      },
    });
    title.anchor.set(0.5, 0);
    title.position.set(w / 2, py + 16);
    this.root.addChild(title);

    // Drops are unidentified until held — the offer stays a mystery.
    const sub = new Text({
      text: 'an unidentified artifact  —  swap what for it?',
      style: { fontFamily: 'monospace', fontSize: 13, fill: Palette.gold },
    });
    sub.anchor.set(0.5, 0);
    sub.position.set(w / 2, py + 44);
    this.root.addChild(sub);

    this.root.addChild(this.focusRing);
    equipped.forEach((artifact, i) => {
      const def = ARTIFACTS_BY_ID.get(artifact.artifactId);
      const brought = runState.broughtIds.has(artifact.artifactId);
      const bound = artifact.curseId !== null;
      const locked = brought || bound;
      const y = py + 78 + i * ROW_H;
      const note = brought ? '   (brought — safe)' : bound ? '   (cursed — bound)' : '';
      const text = new Text({
        text: `${locked ? '🔒 ' : ''}${def?.name ?? artifact.artifactId}` +
          `  Q ${Math.round(artifact.quality * 100)}%${note}`,
        style: {
          fontFamily: 'monospace', fontSize: 14,
          fill: bound ? PROC_COLOR.cursed
            : locked ? Palette.hudDim
              : artifact.proc !== 'none' ? PROC_COLOR[artifact.proc] : Palette.hudText,
        },
      });
      text.position.set(px + 24, y);
      this.root.addChild(text);
      this.rows.push({ y, artifact, locked });
    });
    // Start focus on the first swappable row.
    const firstFree = this.rows.findIndex((r) => !r.locked);
    if (firstFree >= 0) this.focus = firstFree;

    const hint = new Text({
      text: 'swap  [E / Ⓐ]      keep walking  [Esc / Ⓑ]',
      style: { fontFamily: 'monospace', fontSize: 12, fill: Palette.hudDim },
    });
    hint.anchor.set(0.5);
    hint.position.set(w / 2, py + panelH - 22);
    this.root.addChild(hint);
    this.panelX = px;
  }

  private panelX: number;

  enter(): void {}
  exit(): void {}

  update(_dt: number, input: InputSnapshot): void {
    if (this.acceptDelay > 0) {
      this.acceptDelay--;
      return;
    }
    if (input.navY !== 0 && this.rows.length > 0) {
      // Skip locked (brought) rows while navigating.
      let next = this.focus;
      for (let hop = 0; hop < this.rows.length; hop++) {
        next = (next + input.navY + this.rows.length) % this.rows.length;
        if (!this.rows[next]!.locked) break;
      }
      this.focus = next;
    }
    if (input.confirmPressed) {
      const row = this.rows[this.focus];
      if (row && !row.locked) {
        this.popSelf();
        this.onSwap(row.artifact.artifactId);
        return;
      }
    }
    if (input.cancelPressed || input.pausePressed) {
      this.popSelf();
      this.onCancel();
    }
  }

  render(_alpha: number): void {
    const g = this.focusRing;
    g.clear();
    const row = this.rows[this.focus];
    if (!row || row.locked) return;
    g.roundRect(this.panelX + 14, row.y - 6, PANEL_W - 28, ROW_H - 12, 6)
      .stroke({ width: 2, color: Palette.focus });
  }
}
